import { Outlet, useNavigate, useParams } from "react-router-dom"
import { useLocation } from "react-router-dom"
import { MenuItem } from "primereact/menuitem"
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import { Order } from "../../../api/types/order"
import { isOrderEditable, isOrderPrintable, OrderStateTag } from "../StateTag"
import styles from "./styles.module.css"
import { classNames } from "primereact/utils"
import { useContext, useEffect, useState } from "react"
import { Button } from "primereact/button"
import { useCancelOrder } from "../../../api/queries/useCancelOrder"
import { OrderDocumentsModal } from "../DocumentsModal"
import { ConstantsResponseData } from "../../../api/types/constants"
import { useConstants } from "../../../api/queries/useConstants"
import { getLastSuccessStepIndex } from "./validations"
import { useMutation } from "react-query"
import { orderCopyRequest } from "../../../api/queries/order-copy"
import globalToastContext from "../../../context/globalToast"
import { orderOrderTemplateCreateRequest } from "../../../api/queries/order-order-template-create"

export default function OrderForm() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { uuid } = useParams<OrderParams>()
  const { data } = useOrderDetail(uuid)
  const orderDetail: Order | undefined = data?.results.orderDetail
  const { data: constants }: { data: ConstantsResponseData | undefined } = useConstants()
  const { ref: globalToastRef } = useContext(globalToastContext)

  const [orderDocsOrders, setOrderDocsOrders] = useState<string[] | null>(null)
  const onOrderDocumentsModalHide = () => setOrderDocsOrders(null)
  const cancelOrder = useCancelOrder(uuid)

  const steps = [{
    label: "Nakládka",
    url: "loading",
  }, {
    label: "Vykládka",
    url: "unloading",
  }, {
    label: "Zásilka",
    url: "goods",
  }, {
    label: "Kalkulace",
    url: "calculation",
  }, {
    label: "Rekapitulace",
    url: "summary",
  },]

  const [lastSuccessStepIndex, setLastSuccessStepIndex] = useState<number>(0)

  const getOrderDocuments = async () => {
    if (orderDetail) {
      setOrderDocsOrders([orderDetail.uuid])
    }
  }

  const orderOrderTemplate = useMutation({
    mutationFn: () => orderOrderTemplateCreateRequest(uuid)
  })

  const onOrderOrderTemplate = async () => {
    if (window.confirm("Opravdu chcete vytvořit šablonu z objednávky?")) {
      const { results } = await orderOrderTemplate.mutateAsync()

      navigate(`/order-templates/${results.orderTemplateCreate.uuid}`)
    }
  }

  const onOrderStornoClick = async (): Promise<void> => {
    if (window.confirm("Opravdu chcete stornovat objednávku?")) {
      await cancelOrder.mutateAsync()

      navigate("/orders")
    }
  }

  const orderCopy = useMutation({
    mutationFn: () => orderCopyRequest(uuid)
  })

  const onCopy = async () => {
    if (window.confirm("Opravdu chcete vytvořit kopii objednávky?")) {
      const { results } = await orderCopy.mutateAsync()

      globalToastRef?.current?.show({ severity: "success", summary: "Kopie objednávky vytvořena." })

      navigate(`/orders/${results.orderCopy.uuid}/loading`)
    }
  }

  const onStepClick = (item: MenuItem) => {
    const stepIndex = steps.findIndex((step) => step.url === item.url)

    if (stepIndex > -1 && stepIndex <= lastSuccessStepIndex && item.url) {
      navigate(item.url)
    }
  }

  useEffect(() => {
    setLastSuccessStepIndex(getLastSuccessStepIndex(orderDetail))
  }, [pathname, orderDetail])

  const currentURL: string = pathname.split("/").pop() || ""
  const stepIndex: number = steps.findIndex((step) => step.url === currentURL)

  return (
    <>
      <OrderDocumentsModal orders={orderDocsOrders} onHide={onOrderDocumentsModalHide} />

      <div className={"col-12"}>
        <div className={styles.header}>
          <div className={"flex"}>
            {steps.map((step, index) => {
              let cn = classNames({
                [styles.step]: true,
                [styles.stepCurrent]: index === stepIndex,
                [styles.stepActive]: index <= lastSuccessStepIndex,
                [styles.stepToFill]: index === lastSuccessStepIndex,
              })

              return (
                <div key={step.label} className={cn} onClick={() => onStepClick(step)}>
                  <span className={styles.stepLabel}>{step.label}</span>
                  <span className={styles.stepIndex}>{index + 1}</span>
                </div>
              )
            })}
          </div>

          <div className={"flex align-items-center pt-2"}>
            <div className={"flex-1 nowrap"}>
              <i className={styles.headerLabel}>Kód:</i> <strong>{orderDetail?.code || ""}</strong>
            </div>
            <div className={"flex-1 text-center"}>
              <i className={styles.headerLabel}>Stav:</i>
              {" "}
              <OrderStateTag value={orderDetail?.state || 0} options={constants?.results.orderStates || []} />
            </div>
            <div className={"flex-1 text-right"}>
              {orderDetail?.state !== 0 && (
                <Button type="button" label="Kopie" icon="pi pi-copy"
                  className={"p-button p-button-sm p-button-outlined p-button-responsive mr-1"}
                  onClick={onCopy} title="Kopie" />
              )}

              <Button type={"button"} label={"Šablona"} icon={"pi pi-file"}
                className={"p-button p-button-sm p-button-outlined p-button-responsive mr-1"}
                onClick={onOrderOrderTemplate} title={"Šablona"} />

              {isOrderEditable(orderDetail?.state || 0) && (
                <Button type={"button"} label={"Storno"} icon={"pi pi-trash"}
                  className={"p-button p-button-sm p-button-danger p-button-responsive"}
                  onClick={onOrderStornoClick}
                  title={"Storno"} />
              )}

              {isOrderPrintable(orderDetail?.state || 0) && (
                <Button type={"button"} label={"Tisk štítku"} icon={"pi pi-print"}
                  className={"p-button p-button-sm p-button-outlined p-button-responsive"}
                  onClick={getOrderDocuments} title={"Tisk štítku"} />
              )}
            </div>
          </div>
        </div>
      </div>

      <Outlet />
    </>
  )
}

export type OrderParams = "uuid" 
